import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { ButtonGroup } from "dash-styled"
import { useCallback, useEffect, useMemo, useRef } from "react"
import type { PrelabelResponse } from "resha"

import {
    APIJobModel,
    APIJobQueryModel,
    isCancelableJob,
    isRelabelableJob,
    JobStatus,
    JobVersionItem,
    terminalState
} from "../../../../../../../Models/API/JobModel"
import { FilterAndSortExpression } from "../../../../../../../Models/Client/FilterAndSortExpression"
import {
    JobOutputEditCapability,
    PostProcessedOutput
} from "../../../../../../../Models/DataProgram/TemplateMetadata"
import { routePaths } from "../../../../../../../Routes"
import { FlatButton } from "../../../../../../Common/FlatButton/FlatButton"
import { JobActionsGroup } from "./ActionGroup/JobActionsGroup"
import { DetailViewHeader } from "../Header/DetailViewHeader"
import { MainThunkDispatch } from "../../../../../../../Reducers"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { APIAppInstanceModel } from "../../../../../../../Models/API/AppInstanceModel"
import { JobDetailInputOutput } from "./InputOutput_deprecated/JobDetailInputOutput"
import { useShowTaskList } from "../../../../../../../Hooks/useShowTaskList"
import { TaskList } from "../Task/TaskList/TaskList"
import {
    TaskModel,
    TaskListQueryParams,
    UITaskModel
} from "../../../../../../../Models/API/TaskModel"
import { JobDetailViewDetailsSection } from "./DetailsSection_deprecated/DetailsSection"
import {
    startTaskListPoll,
    stopTaskListPoll
} from "../../../../../../../Actions/API/Job/TaskActions"
import {
    hasAppPermission,
    ProjectRole
} from "../../../../../../../Models/Client/ProjectRole"
import { SchemaForm } from "universal-schema/lib"
import { useValues } from "../../../../../../../Hooks/useValues"
import {
    jsonAndFormattedOutputFormats,
    possibleOutputFormats
} from "../../Common/constants"
import { useJobPostProcessAsyncOperation } from "../../../../../../../Hooks/useJobPostProcessAsyncOperation"
import { retryAsyncOperation } from "../../../../../../../Actions/API/AppInstance/AppInstanceActions"

/**
 * @deprecated
 */
export interface JobDetailViewProps {
    orgUsername: string
    appID: string
    job: APIJobModel
    instance: APIAppInstanceModel
    inputSchema: SchemaForm
    outputSchema: SchemaForm
    versionsList: JobVersionItem[]
    selectedVersion: number
    edit: JobOutputEditCapability
    postProcessedData: PostProcessedOutput
    postProcessTitle?: string
    role: ProjectRole[]
    clearFilterOnCloseModal: boolean
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    isMessagesPath?: boolean
    isJobRelabeled: boolean
    prelabels: PrelabelResponse
    showOldVersion: boolean
    wasTaskListToggled?: boolean
    taskListScrollPosition?: number
    setWasTaskListToggled?: (value: boolean) => void
    onDownload?: () => void
    onRelabelRequest?: () => void
    onCancel?: () => void
    onDelete?: () => void
    onAddTag?: (value: string, isNew: boolean) => Promise<void>
    onRemoveTag?: (value: string) => Promise<void>
    onChangeSelectedVersion?: (version: number) => void
    onEnqueue?: () => Promise<void>
    onRelabel?: () => Promise<void>
    handleCancel?: () => Promise<void>
    urlSigner: (url: string) => Promise<string>
    onTaskChange?: (taskID: string) => void
    saveScrollPosition?: (position: number) => void
    enableTaskList?: boolean
    showBreadcrumbs?: boolean
    taskList: UITaskModel[]
}

/**
 * @deprecated
 * @param param0
 * @returns
 */
export const JobDetailView = ({
    orgUsername,
    appID,
    job,
    instance,
    inputSchema,
    outputSchema,
    versionsList,
    selectedVersion,
    edit,
    role,
    clearFilterOnCloseModal,
    filterAndSort,
    isMessagesPath,
    isJobRelabeled,
    prelabels,
    showOldVersion,
    wasTaskListToggled,
    taskListScrollPosition,
    taskList,
    setWasTaskListToggled,
    onDownload,
    onRelabelRequest,
    onCancel,
    onDelete,
    onAddTag,
    onRemoveTag,
    onChangeSelectedVersion,
    onEnqueue,
    onRelabel,
    handleCancel,
    urlSigner,
    onTaskChange,
    saveScrollPosition,
    enableTaskList,
    showBreadcrumbs = true,
    postProcessedData,
    postProcessTitle
}: JobDetailViewProps) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const scrollRef = useRef<HTMLDivElement>(null)
    const showTasksList = useShowTaskList(appID, job.jobType, enableTaskList)
    const postProcessOperation = useJobPostProcessAsyncOperation(
        appID,
        job.uuid
    )
    const handleOperationRetry = useMemo(() => {
        if (postProcessOperation?.status === "FAILED") {
            return async () =>
                dispatch(retryAsyncOperation(appID, postProcessOperation.id))
        }
        return
    }, [postProcessOperation, appID, dispatch])

    const [outputFormat, ...setOutputFormatType] = useValues<
        typeof possibleOutputFormats[number]
    >(
        postProcessedData === "available"
            ? possibleOutputFormats
            : jsonAndFormattedOutputFormats
    )

    const [isDetailShown, , , toggleDetailShown] = useBoolean(false)
    const handleClickRow = useCallback(
        (task: TaskModel) => {
            onTaskChange?.(task.id)
            saveScrollPosition?.(scrollRef.current?.scrollTop || 0)
        },
        [onTaskChange, saveScrollPosition]
    )
    const onEditOutput = useMemo(() => {
        if (
            job.status === JobStatus.COMPLETED &&
            edit !== "not_allowed" &&
            hasAppPermission(role, "job:edit")
        ) {
            return () =>
                dispatch(
                    push(
                        routePaths.jobEdit(orgUsername, appID, job.id, {
                            clearFilterOnCloseModal,
                            ...filterAndSort
                        }),
                        {
                            isMessagesPath
                        }
                    )
                )
        } else {
            return undefined
        }
    }, [
        job.status,
        job.id,
        edit,
        role,
        dispatch,
        orgUsername,
        appID,
        clearFilterOnCloseModal,
        filterAndSort,
        isMessagesPath
    ])

    useEffect(() => {
        const autoscrollDelay = setTimeout(() => {
            scrollRef.current?.scrollTo?.({
                top: taskListScrollPosition,
                behavior: "smooth"
            })
        }, 500)

        return () => {
            clearTimeout(autoscrollDelay)
        }
    }, [taskListScrollPosition])

    const currentJob = versionsList[selectedVersion]

    const handleRequestTaskDetail = useCallback(
        (taskId: number) => {
            window.open(
                routePaths.taskDetails(
                    orgUsername,
                    appID,
                    job.id,
                    String(selectedVersion),
                    String(taskId)
                ),
                "_blank",
                "noopener,noreferrer"
            )
        },
        [appID, job.id, orgUsername, selectedVersion]
    )

    useEffect(() => {
        dispatch(
            startTaskListPoll(currentJob.versionJobId, TaskListQueryParams)
        )

        return () => {
            dispatch(stopTaskListPoll())
        }
    }, [currentJob.versionJobId, dispatch])

    return (
        <>
            <DetailViewHeader
                orgUsername={orgUsername}
                job={job}
                instance={instance}
                sort={filterAndSort.sort}
                versionsList={versionsList}
                selectedVersion={selectedVersion}
                showOldVersion={showOldVersion}
                onAddTag={onAddTag}
                onRemoveTag={onRemoveTag}
                onChangeSelectedVersion={onChangeSelectedVersion}
                showBreadcrumbs={showBreadcrumbs}
            />
            <div className="flex justify-end flex-shrink-0">
                {currentJob !== undefined ? (
                    <JobActionsGroup
                        className="mr-2"
                        onDownload={onDownload}
                        onEdit={
                            currentJob.isLatestVersion
                                ? onEditOutput
                                : undefined
                        }
                        onRelabelRequest={
                            hasAppPermission(role, "job:relabel") &&
                            currentJob.isLatestVersion &&
                            selectedVersion === 0 &&
                            isRelabelableJob(job)
                                ? onRelabelRequest
                                : undefined
                        }
                        onCancel={
                            hasAppPermission(role, "job:cancel") &&
                            currentJob.isLatestVersion &&
                            selectedVersion === 0 &&
                            isCancelableJob(job)
                                ? onCancel
                                : undefined
                        }
                        onDelete={
                            hasAppPermission(role, "job:delete") &&
                            !isJobRelabeled
                                ? onDelete
                                : undefined
                        }
                    />
                ) : null}
                <ButtonGroup equalWidth={false}>
                    <FlatButton
                        onClick={
                            postProcessedData === "available"
                                ? setOutputFormatType[1]
                                : setOutputFormatType[0]
                        }
                        className={
                            outputFormat === "formatted" ? "bg-white" : ""
                        }
                    >
                        Formatted
                    </FlatButton>
                    <FlatButton
                        onClick={
                            postProcessedData === "available"
                                ? setOutputFormatType[2]
                                : setOutputFormatType[1]
                        }
                        className={outputFormat === "json" ? "bg-white" : ""}
                    >
                        JSON
                    </FlatButton>
                    {postProcessedData === "available" ? (
                        <FlatButton
                            onClick={setOutputFormatType[0]}
                            className={
                                outputFormat === "processed" ? "bg-white" : ""
                            }
                        >
                            Processed
                        </FlatButton>
                    ) : null}
                </ButtonGroup>
            </div>
            <div
                ref={scrollRef}
                className="flex flex-col flex-grow overflow-hidden overflow-y-scroll mt-4"
            >
                <JobDetailInputOutput
                    key={
                        job.id /* force re-render on job id change, so that internal state of TaskItem components get cleared */
                    }
                    job={job}
                    inputSchema={inputSchema}
                    outputSchema={outputSchema}
                    instance={instance}
                    selectedVersion={currentJob}
                    outputFormatType={outputFormat}
                    postProcessedData={postProcessedData}
                    postProcessTitle={postProcessTitle}
                    role={role}
                    showOldVersion={showOldVersion}
                    prelabels={prelabels}
                    onEnqueue={onEnqueue}
                    onRelabel={onRelabel}
                    handleCancel={handleCancel}
                    urlSigner={urlSigner}
                    onRequestTaskDetail={handleRequestTaskDetail}
                    postProcessOperation={postProcessOperation}
                    onOperationRetry={handleOperationRetry}
                />
                {showTasksList && currentJob !== undefined ? (
                    <TaskList
                        appID={appID}
                        isJobInTerminalState={terminalState().includes(
                            currentJob.status
                        )}
                        wasTaskListToggled={wasTaskListToggled}
                        setWasTaskListToggled={setWasTaskListToggled}
                        onClick={handleClickRow}
                        taskList={taskList}
                    />
                ) : null}
                <JobDetailViewDetailsSection
                    job={job}
                    versionsList={versionsList}
                    isOpen={isDetailShown}
                    onToggleOpen={toggleDetailShown}
                />
            </div>
        </>
    )
}
